/**
 * Generates a displacement map for the liquid-glass refraction effect.
 *
 * The map is a PNG where the red channel encodes horizontal pixel displacement
 * and the green channel encodes vertical displacement (128 = no shift, 0 = -scale,
 * 255 = +scale). `feDisplacementMap` reads this map and bends the content beneath
 * the glass accordingly — like light refracting through a real lens.
 *
 * The refraction is concentrated in a "bezel" band near the rounded-rect edge and
 * fades to zero in the centre, so the middle of the glass stays optically clear
 * (clear, not blurry) while the rim bends the background — the signature of Apple's
 * Liquid Glass, and what distinguishes it from flat frosted-blur "glassmorphism".
 */

/** Signed distance to a rounded rectangle. Negative inside, 0 on the border. */
function roundedRectSDF(
  px: number,
  py: number,
  halfW: number,
  halfH: number,
  radius: number,
): number {
  const qx = Math.abs(px) - (halfW - radius);
  const qy = Math.abs(py) - (halfH - radius);
  const ax = Math.max(qx, 0);
  const ay = Math.max(qy, 0);
  return Math.min(Math.max(qx, qy), 0) + Math.sqrt(ax * ax + ay * ay) - radius;
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export interface DisplacementMapOptions {
  /** Glass width in CSS px. */
  width: number;
  /** Glass height in CSS px. */
  height: number;
  /** Corner radius in CSS px. */
  radius: number;
  /** Width of the refracting bezel band, in CSS px. Larger = thicker glass rim. */
  bezel: number;
  /**
   * Edge profile exponent. 1 = linear ramp, >1 pushes refraction tighter to the
   * very edge (sharper, more "convex lens"); <1 spreads it inward (softer).
   */
  profile?: number;
}

/**
 * Builds the displacement map as a data URL. Must run client-side (uses canvas).
 * Computed at a capped resolution; `feImage` stretches it to the element, which is
 * fine — the vector field is smooth.
 */
export function generateDisplacementMap({
  width,
  height,
  radius,
  bezel,
  profile = 1.6,
}: DisplacementMapOptions): string {
  // Cap the map resolution for perf; the field is smooth enough to upscale.
  const cap = 240;
  const downscale = Math.min(1, cap / Math.max(width, height));
  const cw = Math.max(1, Math.round(width * downscale));
  const ch = Math.max(1, Math.round(height * downscale));
  const r = radius * downscale;
  const b = Math.max(1, bezel * downscale);

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const image = ctx.createImageData(cw, ch);
  const data = image.data;
  const halfW = cw / 2;
  const halfH = ch / 2;

  const sdf = (px: number, py: number) =>
    roundedRectSDF(px - halfW, py - halfH, halfW, halfH, r);

  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      const d = sdf(x + 0.5, y + 0.5); // negative inside
      const dist = -d; // distance from the edge, positive inside the shape

      let dx = 0;
      let dy = 0;

      if (dist >= 0 && dist < b) {
        // Outward surface normal via finite differences of the SDF.
        const gx = sdf(x + 1, y) - sdf(x - 1, y);
        const gy = sdf(x, y + 1) - sdf(x, y - 1);
        const gl = Math.hypot(gx, gy) || 1;
        const nx = gx / gl;
        const ny = gy / gl;

        // 1 at the very edge, 0 at depth `b` inward.
        const t = 1 - dist / b;
        const mag = Math.pow(t, profile);

        // Pull the sampled background inward toward the centre at the rim — this
        // is what visually compresses/bends the backdrop like a lens edge.
        dx = -nx * mag;
        dy = -ny * mag;
      }

      const idx = (y * cw + x) * 4;
      data[idx] = clamp255(128 + dx * 127); // R = x displacement
      data[idx + 1] = clamp255(128 + dy * 127); // G = y displacement
      data[idx + 2] = 128; // B unused
      data[idx + 3] = 255; // opaque
    }
  }

  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL();
}
